import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { panelStyle } from "../../../shared/ui/card-frame";
import { BUTTON_STYLE, SOFT } from "./soft-tokens";

/**
 * The AI's mark in the chat kit: a navy coin with a gold spark, the same at
 * every size. Still, on purpose: a mark that moves while it has nothing to
 * say is the widget people learn to ignore.
 */
export function AiMark({ size = 28 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full"
      style={{
        width: size,
        height: size,
        background: SOFT.inkGradient,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
        color: SOFT.gold,
      }}
    >
      <Icon name="ri-sparkling-2-fill" size={Math.round(size * 0.5)} />
    </span>
  );
}

/**
 * What the chat already knows about the trip, when it knows it. Every field
 * is optional and nothing is ever guessed: a chip the guest did not give us
 * is the interface putting words in their mouth.
 */
export type WelcomeTripContext = {
  /** A city name, already localised, e.g. "Marrakech". */
  city?: string | null;
  /** The dates, already formatted for the guest's locale. */
  dates?: string | null;
  /** The party, already formatted, e.g. "2 adults". */
  party?: string | null;
};

/** The four openers. Existing keys, present in all seven locales. */
const STARTER_KEYS = [
  "ai_concierge.starter_kids",
  "ai_concierge.starter_sunset",
  "ai_concierge.starter_hammam",
  "ai_concierge.starter_rainy",
] as const;

/**
 * Chat kit C1, the welcome: the mark, "Hi, I am MaJourneys AI" and one line,
 * the trip context chips when the chat already knows them, and four starters
 * in a 2x2 grid.
 *
 * The starters live here and only here. They are the one place the chat may
 * offer words before the guest has said anything; under an answer they would
 * be the interface writing the guest's next line (owner rule, 2026-09-08).
 * They only draw when the host passes `onPick`, so a host that cannot send
 * never shows a button that does nothing.
 */
export function ChatWelcome({
  compact = false,
  stack = false,
  onPick,
  context,
}: {
  compact?: boolean;
  /**
   * The phone's full-screen chat (option C, 2026-10-12): no card, a larger
   * greeting, and the starters as full-width rows, read top to bottom right
   * above the message box.
   */
  stack?: boolean;
  /** Sends (or fills) a starter, through the host's own submit. */
  onPick?: (query: string) => void;
  /** Trip facts the host already has. Omitted chips are simply not drawn. */
  context?: WelcomeTripContext;
}) {
  const { t } = useTranslation();
  const chips = [
    context?.city ? { icon: "ri-map-pin-line", label: context.city } : null,
    context?.dates ? { icon: "ri-calendar-line", label: context.dates } : null,
    context?.party ? { icon: "ri-group-line", label: context.party } : null,
  ].filter((c): c is { icon: string; label: string } => c !== null);

  if (stack) {
    return (
      <section className="mj-rise-in" data-testid="chat-welcome">
        <AiMark size={42} />
        <h3 className="mt-3 text-start text-[22px] font-bold leading-7" style={{ color: SOFT.ink }}>
          {t("ai_concierge.welcome_hi", { defaultValue: "Hi, I am MaJourneys AI" })}
        </h3>
        <p className="mt-1 text-start text-[14px] font-medium leading-5" style={{ color: SOFT.body }}>
          {t("ai_concierge.home_ask", { defaultValue: "Where are you going, and for how long?" })}
        </p>
        {onPick ? (
          <div className="mt-4 flex flex-col gap-2">
            {STARTER_KEYS.map((key) => {
              const label = t(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onPick(label)}
                  className="mj-press flex min-h-[48px] items-center justify-between gap-3 rounded-[16px] px-4 py-3 text-start text-[14px] font-semibold leading-5"
                  style={{ ...BUTTON_STYLE, color: SOFT.ink }}
                >
                  {label}
                  <Icon name="ri-arrow-right-up-line" size={16} aria-hidden="true" className="shrink-0 rtl:-scale-x-100" />
                </button>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className="mj-rise-in"
      style={{ ...panelStyle(22, "#fff"), padding: compact ? 14 : 16 }}
      data-testid="chat-welcome"
    >
      <div className="flex items-center gap-3">
        <AiMark size={compact ? 34 : 38} />
        <div className="min-w-0">
          <h3 className="text-start text-[15px] font-semibold leading-5" style={{ color: SOFT.ink }}>
            {t("ai_concierge.welcome_hi", { defaultValue: "Hi, I am MaJourneys AI" })}
          </h3>
          <p className="text-start text-[12.5px] leading-[18px]" style={{ color: SOFT.body }}>
            {t("ai_concierge.home_ask", { defaultValue: "Where are you going, and for how long?" })}
          </p>
        </div>
      </div>

      {chips.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="welcome-context">
          {chips.map((c) => (
            <li
              key={c.icon}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold"
              style={{ background: SOFT.tint, color: SOFT.ink }}
            >
              <Icon name={c.icon} size={12} aria-hidden="true" />
              {c.label}
            </li>
          ))}
        </ul>
      ) : null}

      {onPick ? (
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {STARTER_KEYS.map((key) => {
            const label = t(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onPick(label)}
                className="mj-press min-h-[40px] rounded-[12px] px-3 py-2 text-start text-[12.5px] font-medium leading-[17px]"
                style={{ ...BUTTON_STYLE, color: SOFT.ink }}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
